import type { Catalog } from "../../en";
import { workflowsP3 } from "./workflows-p3";
import { workflowsTriggers } from "./workflows-triggers";
import { workflowsConnectors } from "./workflows-p5-connectors";
import { workflowsConnectorStep } from "./workflows-connector";
import { workflowsContent } from "./workflows-content";
import { workflowsDefaults } from "./workflows-defaults";
import { workflowsSystem } from "./workflows-system";
import { workflowsService } from "./workflows-service";
import { workflowsCustomer } from "./workflows-customer";
import { workflowsConversation } from "./workflows-conversation";
import { workflowsP4 } from "./workflows-p4";
import { workflowsAddStep } from "./workflows-add-step";
import { workflowsExtras } from "./workflows-extras";
import { workflowsFollowups } from "./workflows-followups";
import { workflowsOutcomes } from "./workflows-outcomes";
import { workflowsSetup } from "./workflows-setup";
import { workflowsVerify } from "./workflows-verify";
import { workflowsRegression } from "./workflows-regression";

/** Filipino: Workflows (M26). */
export const workflows: Catalog["engage"]["workflows"] = {
  title: "Workflows",
  subtitle: "Ikaw ang magpapasya kung ano ang mangyayari, kailan, at para kanino. Nagsisimula ang workflow sa isang trigger, sinusuri kung para kanino ito, saka pinapatakbo ang mga hakbang — mag-tag, mag-assign, abisuhan ang team, o magpadala ng webhook kapag kailangan lang.",
  planNeeded: "Plan na {{plan}}",
  planFeature: "Workflows",
  newWorkflow: "Bagong workflow",
  empty: {
    title: "Wala pang workflow",
    body: "Magsimula sa isang trigger, gaya ng nakumpirmang booking o bagong lead, at piliin ang susunod na mangyayari.",
  },
  kpis: {
    live: "Mga live na workflow",
    liveOf: "{{count}} sa {{limit}} sa inyong plano",
    liveUnlimited: "Walang limitasyon sa inyong plano",
    runs: "Mga run (30 araw)",
    completed: "Natapos",
    failed: "Mga pumalyang run",
  },
  filters: {
    agent: "Agent",
    allAgents: "Lahat ng agent",
    none: "Wala pang workflow para sa agent na ito.",
  },
  loadFailed: "Hindi namin ma-load ang mga workflow mo.",
  notFound: {
    title: "Hindi namin makita ang workflow na iyon",
    body: "Maaaring natanggal na ito, o mali ang link.",
  },
  groups: {
    start: "Simula ng usapan",
    during: "Habang nag-uusap",
    staff: "Staff at routing",
    leads: "Leads at bookings",
    other: "Reusable at iba pa",
    ...workflowsTriggers.groups,
  },
  groupNote: "Sa ganitong pagkakasunod tumatakbo ang mga workflow ng isang trigger.",
  columns: { title: "Pamagat", kind: "Uri", state: "Estado", channels: "Mga channel", updated: "Binago", sent: "Naipadala", completed: "Natapos", failed: "Pumalya", order: "Ayos", ...workflowsP4.columns },
  goalTitle: workflowsP4.goalTitle,
  moveUp: "Itaas ang {{name}}",
  moveDown: "Ibaba ang {{name}}",
  reordered: "Na-save ang pagkakasunod",
  allChannels: "Lahat ng channel",
  kinds: {
    facing: "Para sa customer",
    background: "Sa background",
    facingOnly: "Para sa customer",
    either: "Para sa customer o background",
    ...workflowsTriggers.kinds,
  },
  facingNote: "Ang unang tumutugmang workflow na para sa customer lang ang tumatakbo. Itaas ang gusto mong mauna.",
  mixedNote: "Para sa customer: ang unang tumugma lang ang tumatakbo. Lahat ng background workflow ay tumatakbo, ayon sa ayos.",
  states: { draft: "Draft", live: "Live", paused: "Naka-pause" },
  unpublished: "May hindi pa na-publish na pagbabago",
  updatedBy: "{{when}} · {{name}}",
  triggers: {
    ...workflowsP3.triggers,
    ...workflowsTriggers.triggers,
    ...workflowsP4.triggers,
    widget_opened: "Binuksan ng bisita ang widget",
    home_option: "Pumili ang bisita ng Home option",
    visitor_first_message: "Unang mensahe ng bisita",
    visitor_message: "Anumang mensahe ng bisita",
    assistant_replied: "Sumagot ang assistant",
    assistant_could_not_answer: "Hindi masagot ng assistant",
    intent_detected: "Natukoy ang pakay ng bisita",
    staff_replied: "Sumagot ang staff",
    feedback_negative: "Hindi nakatulong ayon sa bisita",
    handoff_requested: "Humiling ng tao",
    conversation_assigned: "Na-assign ang usapan",
    conversation_status_changed: "Nagbago ang estado ng usapan",
    lead_created: "Bagong lead",
    lead_updated: "Binago ng staff ang lead",
    booking_requested: "Humiling ng booking",
    booking_confirmed: "Nakumpirma ang booking",
    booking_declined: "Tinanggihan ang booking",
    booking_cancelled: "Kinansela ang booking",
    action_run_finished: "Natapos ang action run",
    source_processed: "Na-sync ang knowledge source",
    source_failed: "Pumalya ang knowledge source",
  },
  triggerHints: {
    ...workflowsP3.triggerHints,
    ...workflowsTriggers.triggerHints,
    ...workflowsP4.triggerHints,
    widget_opened: "Batiin sila gamit ang reply buttons bago pa sila mag-type.",
    home_option: "Tumatakbo kapag pinindot ang isang card sa Widget Home.",
    assistant_could_not_answer: "Mag-alok ng tao, booking o lead form.",
    intent_detected: "Booking, presyo, makausap ang tao … bago ang sagot.",
    action_run_finished: "Sundan ang resulta ng isang custom na action.",
    visitor_first_message: "Ayusin o i-route ang unang tanong ng usapan.",
    visitor_message: "I-tag o ipasa ang bawat mensahe ng bisita.",
    assistant_replied: "Tumugon sa mga sagot ng assistant.",
    staff_replied: "Tumugon sa mga sagot ng inyong team.",
    feedback_negative: "Mag-follow up kapag hindi nakatulong ang sagot.",
    handoff_requested: "I-route, abisuhan, at i-alert ang chat apps ninyo.",
    conversation_assigned: "Tumugon sa bagong assignee o team.",
    conversation_status_changed: "Magpatakbo ng hakbang kapag na-resolve o naisara ang usapan.",
    lead_created: "Maglagay ng may-ari, mag-sync sa CRM, i-alert ang sales.",
    lead_updated: "Tumugon kapag binago ng staff ang stage o may-ari.",
    booking_requested: "Ipaalam sa team ang bagong request.",
    booking_confirmed: "Ipadala lang ang mga nakumpirmang booking sa inyong system.",
    booking_declined: "Mag-follow up sa mga tinanggihang booking.",
    booking_cancelled: "Ayusin ang mga kailangan pagkatapos ng pagkansela.",
    source_processed: "Ipaalam sa team kapag tapos na ang sync.",
    source_failed: "Ma-alert kapag pumalya ang sync.",
  },
  create: {
    title: "Gumawa ng workflow",
    subtitle: "Piliin ang trigger. Hindi na ito mababago pagkatapos.",
    name: "Pangalan",
    namePlaceholder: "hal. Nakumpirmang tour papunta sa registrar",
    nameRequired: "Lagyan ng pangalan ang workflow.",
    nameTooLong: "Gawing {{max}} karakter o mas kaunti ang pangalan.",
    submit: "Gumawa ng workflow",
    created: "Nagawa ang workflow bilang draft",
    mode: "Magsimula sa",
    fromTrigger: "Isang trigger",
    fromTemplate: "Isang template",
    templatesTitle: "Magsimula sa isang template",
    starter: "Kumusta! Paano po namin kayo matutulungan ngayon?",
  },
  templates: {
    title: "Mga template",
    note: "Draft ang bubukas sa bawat template. Walang tatakbo hangga't hindi ninyo ito ginagawang live.",
    ...workflowsP4.templates,
  },
  builder: {
    customSchedule: workflowsP3.customSchedule,
    options: { ...workflowsP3.options, ...workflowsTriggers.options },
    tabs: { ...workflowsP4.tabs, tests: workflowsRegression.tab },
    back: "Bumalik sa workflows",
    heading: "Tagabuo ng workflow",
    more: "Higit pa",
    save: "I-save ang draft",
    saved: "Na-save ang draft",
    publish: "I-publish",
    published: "Na-publish. Bersyon {{version}} na ang tatakbo sa mga bagong event.",
    pause: "I-pause",
    paused: "Naka-pause. Tatapusin ang mga tumatakbong run.",
    resume: "Ituloy",
    resumed: "Live na ulit",
    delete: "Burahin",
    deleteLive: "Burahin (i-pause muna)",
    deleteTitle: "Burahin ang {{name}}?",
    deleteBody: "Mabubura rin ang mga run at estadistika nito. Hindi na ito maibabalik.",
    deleted: "Nabura ang workflow",
    problems: "Ayusin muna ang mga ito bago mag-save:",
    unsaved: "May mga pagbabagong hindi pa na-save.",
    readOnly: "Makikita mo ang workflow na ito. Hilingin sa admin na baguhin ito.",
    cannotPublish: "Ang mga may pahintulot lang mag-publish ang makakapag-live ng workflow.",
    version: "v{{version}}",
    trigger: "Trigger",
    settings: "Sino at kailan",
    agents: "Mga agent",
    allAgents: "Lahat ng agent",
    channels: "Mga channel",
    schedule: "Kailan",
    schedules: { any: "Anumang oras", office_hours: "Habang bukas ang opisina", outside_office_hours: "Sarado ang opisina", custom: "Custom na lingguhang iskedyul" },
    conditions: "Kapag lang",
    conditionsHint: "Iwanang walang laman para tumakbo sa lahat ng pinapatakbo ng trigger na ito.",
    intentHint: "Piliin kung aling pakay ang magsisimula ng workflow na ito: magdagdag ng kondisyon sa “Natukoy na pakay” ({{actions}}) o “Natukoy na paksa”.",
    path: "Path {{id}}",
    pathFrom: "Nagsisimula sa trigger",
    addPath: "Magdagdag ng path",
    removePath: "Alisin ang Path {{id}}",
    pathLabel: "Pangalan ng path",
    addStep: "Magdagdag ng hakbang",
    noSteps: "Wala pang hakbang. Idagdag ang una.",
    editStep: "I-edit ang hakbang",
    removeStep: "Alisin ang hakbang",
    moveStepUp: "Itaas ang hakbang",
    moveStepDown: "Ibaba ang hakbang",
    done: "Tapos",
    stepName: "Pangalan ng hakbang",
    stepNameHint: "Ginagamit sa run logs. Letra, numero at _.",
    stepNameInvalid: "Letra, numero, espasyo at _ lang.",
    incomplete: {
      ...workflowsP3.incomplete,
      ...workflowsP4.incomplete,
      ...workflowsConnectorStep.incomplete,
      ...workflowsConversation.incomplete,
      ...workflowsContent.incomplete,
      ...workflowsService.incomplete,
      ...workflowsSystem.incomplete,
      message: "Isulat ang makikita ng bisita",
      buttonLabels: "Kailangan ng label ang bawat button",
      buttonPaths: "Piliin kung saan papunta ang bawat button",
      fieldLabel: "Pangalanan ang field",
      options: "Idagdag ang mga pagpipilian",
      saveKey: "Pangalanan ang field na paglalagyan",
      action: "Piliin kung aling action ang ipapakita",
      person: "Piliin kung kanino ipapasa",
    },
    preview: {
      open: "I-preview",
      title: "Preview · draft",
      description: "Isang test na usapan gamit ang draft mo. Walang webhook na ipapadala at walang bibilangin.",
      banner: "Preview — hindi ipinapadala ang mga webhook, email at notification.",
      agent: "Agent",
      agentHint: "Ang agent na ito ang kausap sa test na usapan.",
      restart: "Ulitin",
      loading: "Sinisimulan ang preview",
      failedTitle: "Hindi masimulan ang preview",
      waitsForMessage: "Nagsisimula ang workflow na ito kapag nagsulat ang bisita. Mag-type ng mensahe sa ibaba para subukan.",
      noRun: "Walang hakbang ng workflow na tumakbo para sa mensaheng ito. Hindi tumugma ang mga kondisyon nito, o sinagot ito ng assistant gaya ng dati.",
      notStarted: "Hindi nagsimula ang workflow. Tingnan kung para kanino at kailan ito tumatakbo, saka ulitin.",
      conversation: "Preview na usapan",
      placeholder: "Sumulat bilang bisita",
      footer: "Sagot lang ng assistant ang gumagamit ng test credits. Walang ginagamit ang mga hakbang ng workflow.",
      pathTaken: "Path na dinaanan: {{path}}",
      outcome: workflowsOutcomes.preview.outcome,
      dry: {
        title: "Dry run · draft",
        description: "Ang mga hakbang na gagawin ng workflow na ito para sa sample na datos. Walang ipapadala o babaguhin.",
        banner: "Dry run — walang ipapadala o babaguhin.",
        facts: "Sample na datos",
        channel: "Channel",
        officeHours: "Oras ng opisina",
        open: "Bukas",
        closed: "Sarado",
        message: "Mensahe ng bisita (opsyonal)",
        messageHint: "Ginagamit ng mga kondisyong bumabasa ng teksto ng mensahe.",
        run: "Ipakita ang mga hakbang",
        failedTitle: "Hindi natapos ang dry run",
        runs: "Tatakbo ang workflow na ito para sa mga datos na ito.",
        wouldNot: "Hindi magsisimula ang workflow na ito para sa mga datos na ito.",
        mismatch: {
          agent: "Para ito sa ibang mga agent.",
          channel: "Hindi ito nakatakda para sa channel na ito.",
          schedule: "Hindi tugma ang iskedyul nito sa oras ng opisinang pinili mo.",
          conditions: "Hindi tumutugma ang mga kondisyon nitong “Kapag lang”.",
        },
        paths: "Mga path na dinaanan: {{paths}}",
        pathHeading: "Path {{id}}",
        pathHeadingNamed: "Path {{id}} · {{label}}",
        branch: "Dadaan sa branch na “{{label}}”",
        branchElse: "Walang tumutugmang branch: dadaan sa else path",
        rules: "Mga tumutugmang rule: {{rules}}",
        rulesNone: "Walang tumutugmang rule",
        goTo: "Magpapatuloy sa Path {{path}}",
        ended: "Dito nagtatapos ang run",
        snoozedUntil: "I-snooze ang usapan hanggang {{time}}",
        waitSkipped: "Walang hihintayin, kaya nagpapatuloy ito: {{reason}}",
        waitsUntil: "Maghihintay hanggang {{time}}, kapag nagbukas ang opisina",
        snoozeSkipped: "Walang dapat i-snooze, kaya tuloy lang: {{reason}}",
        snoozeSkips: { past: "lumipas na ang oras na iyon", office_open: "bukas na ang opisina ngayon", no_office_hours: "walang oras ng opisina na hihintayin" },
        inRule: "Mula sa rule na “{{rule}}”",
        tooLong: "Huminto ang paglalakad sa limitasyon ng mga hakbang.",
        empty: "Walang hakbang na tatakbo.",
        steps: "Mga hakbang ayon sa pagkakasunod",
      },
    },
    channelNotes: {
      title: "Paano ipinapakita ng mga channel ang mga hakbang para sa customer",
      messenger: "Ipinapakita ng Messenger ang reply buttons bilang quick replies: hanggang {{replies}} lang, at pinuputol ang label sa {{chars}} na character. Maglagay ng Messenger label sa mahahabang button.",
      email: "Ipinapakita ng email ang reply buttons bilang may-numerong pagpipilian; numero ang isasagot ng bisita. Nilalaktawan ang Collect data sa email.",
      api: "Hindi pinapatakbo ng developer API ang mga hakbang na nakikita ng bisita: diretso sa assistant ang usapang dumaan sa API.",
      steps: { ...workflowsConversation.builder.channelNotes.steps, ...workflowsVerify.builder.channelNotes.steps, ...workflowsContent.channelNotes.steps },
    },
  },
  stepGroups: {
    visitorLocked: "Kailangan ng trigger na nakikita ng bisita, gaya ng “Binuksan ng bisita ang widget”.",
    waitLocked: "Kailangan ng usapan ng hakbang na ito, kaya hindi ito magagamit sa mga trigger na tumatakbo bago magsulat ang bisita.",
  },
  steps: {
    ...workflowsP3.steps,
    ...workflowsP4.steps,
    ...workflowsConnectorStep.steps,
    ...workflowsConversation.steps,
    ...workflowsContent.steps,
    ...workflowsVerify.steps,
    ...workflowsSystem.steps,
    ...workflowsService.steps,
    send_webhook: "Magpadala ng webhook",
    add_tags: "Magdagdag ng tags",
    remove_tags: "Mag-alis ng tags",
    assign: "I-assign",
    set_status: "Itakda ang estado",
    add_note: "Magdagdag ng internal note",
    update_lead: "I-update ang lead",
    notify_staff: "Abisuhan ang staff",
    send_message: "Magpadala ng mensahe",
    reply_buttons: "Reply buttons",
    collect_data: "Kumuha ng detalye",
    let_assistant_answer: "Hayaang sumagot ang assistant",
    show_booking: "Ipakita ang booking card",
    show_lead_form: "Ipakita ang lead form",
    hand_to_person: "Ipasa sa tao",
    expected_reply_time: "Ipakita ang inaasahang oras ng sagot",
    branch: "Branch",
    apply_rules: "Ilapat ang rules",
    go_to_path: "Pumunta sa path",
    end: "Tapusin",
  },
  summaries: {
    ...workflowsP3.summaries,
    ...workflowsP4.summaries,
    ...workflowsConnectorStep.summaries,
    ...workflowsConversation.summaries,
    ...workflowsContent.summaries,
    ...workflowsVerify.summaries,
    ...workflowsFollowups.summaries,
    ...workflowsSystem.summaries,
    send_webhook: "Papunta sa {{endpoint}}",
    tags: "{{tags}}",
    assignTeam: "Sa team {{name}}",
    assignUser: "Kay {{name}}",
    unassign: "Alisin ang assignee",
    set_status: "Markahang {{status}}",
    update_lead: "{{changes}}",
    leadStage: "Stage → {{stage}}",
    leadOwner: "May-ari → {{name}}",
    leadSync: "I-sync sa CRM",
    notify: "{{who}}: {{title}}",
    branch_one: "{{count}} branch · kung wala → {{else}}",
    branch_other: "{{count}} branch · kung wala → {{else}}",
    rules_one: "{{count}} rule",
    rules_other: "{{count}} rules",
    go_to_path: "Ituloy sa Path {{path}}",
    end: "Huminto rito",
    endRun: "tapusin",
    unknown: "Hindi pa nakatakda",
    collect: "{{question}} ({{type}}, ise-save sa {{target}})",
    answer: "Sasagot mula sa inyong knowledge, may mga source",
    answerGuided: "Sasagot mula sa inyong knowledge · {{guidance}}",
    booking: "Booking card · {{action}}",
    leadForm: "Lead form · {{action}}",
    handDefault: "Default na routing",
    replyTime: "Sasabihin sa bisita kung kailan aasahan ang sagot",
    typedInstead: "Kung nag-type na lang → sasagot ang assistant",
    exits: {
      ...workflowsP3.exits,
      ...workflowsP4.exits,
      ...workflowsConnectorStep.exits,
      ...workflowsConversation.exits,
      ...workflowsVerify.exits,
      ...workflowsFollowups.exits,
      ...workflowsTriggers.exits,
      to: "{{label}} → {{path}}",
      branch: "Branch {{n}}",
      else: "Kung wala",
      button: "Button {{n}}",
      answered: "Nasagot",
      notAnswered: "Hindi nasagot",
      askedForPerson: "Humiling ng tao",
      booked: "Na-book o nag-request",
      notBooked: "Hindi na-book",
      submitted: "Naipadala ang detalye",
    },
  },
  fields: {
    ...workflowsP3.fields,
    ...workflowsP4.fields,
    ...workflowsConversation.fields,
    ...workflowsVerify.fields,
    ...workflowsFollowups.fields,
    ...workflowsSystem.fields,
    endpoint: "Webhook endpoint",
    endpointHint: "May pirma, inuulit kapag pumalya, at naka-log gaya ng anumang webhook. Ayusin ang mga endpoint sa Settings → Webhooks.",
    noEndpoints: "Wala pang webhook endpoint.",
    openWebhooks: "Magdagdag sa Settings",
    body: "Laman",
    bodies: { standard: "Karaniwang event", custom: "Sariling JSON" },
    template: "Sariling JSON",
    templateHint: "Ilagay ang bawat variable sa loob ng quotes, gaya ng halimbawa. Ini-escape ang mga value para sa iyo.",
    message: "Mensahe sa chat (Slack / Discord)",
    messageHint: "Ang ipapakita ng mga Slack at Discord endpoint. Gumagana rito ang mga variable.",
    variables: "Mga variable",
    tags: "Tags",
    tagsHint: "Hinihiwalay ng kuwit.",
    tagsCut: "Hanggang {{max}} tag na may {{chars}} character bawat isa. Hindi isinama ang sobra.",
    assignTo: "I-assign sa",
    assignTargets: { team: "Isang team", user: "Isang tao", unassign: "Walang tao (alisin)" },
    team: "Team",
    member: "Miyembro",
    choose: "Pumili…",
    status: "Estado",
    statuses: { open: "Bukas", waiting: "Naghihintay", resolved: "Nalutas", closed: "Sarado" },
    note: "Note",
    noteHint: "Staff lang ang makakakita. Gumagana rito ang mga variable.",
    stage: "Ilipat sa stage",
    owner: "Itakda ang may-ari",
    unchanged: "Huwag baguhin",
    syncCrm: "I-sync sa nakakonektang CRM o Google Sheet",
    notifyWho: "Abisuhan",
    notifyTargets: { owners_admins: "Mga Owner at Admin", assignee: "Ang assignee", lead_owner: "Ang may-ari ng lead", team: "Isang team", user: "Isang tao" },
    title: "Pamagat",
    notifyBody: "Mensahe",
    email: "Magpadala rin ng email",
    branchLabel: "Pangalan ng branch",
    goTo: "Saka pumunta sa",
    elseGoTo: "Kung walang tumugma",
    endHere: "Tapusin ang run",
    addBranch: "Magdagdag ng branch",
    removeBranch: "Alisin ang branch",
    ruleLabel: "Pangalan ng rule",
    addRule: "Magdagdag ng rule",
    removeRule: "Alisin ang rule",
    ruleActions: "Gawin",
    addAction: "Magdagdag ng aksyon",
    newPath: "Bagong path",
    choosePath: "Pumili ng path",
    visitorMessage: "Mensahe",
    messageMarkdownHint: "Gumagana ang bold, italic at links.",
    buttons: "Mga button ({{min}}–{{max}})",
    buttonLabelN: "Label ng button {{n}}",
    buttonGoesToN: "Saan papunta ang button {{n}}",
    moveButtonUp: "Itaas ang button {{n}}",
    moveButtonDown: "Ibaba ang button {{n}}",
    removeButtonN: "Alisin ang button {{n}}",
    addButton: "Magdagdag ng button",
    messengerLabelN: "Messenger label ng button {{n}}",
    messengerLabelPlaceholder: "Mas maikling label para sa Messenger",
    removeMessengerLabelN: "Alisin ang Messenger label ng button {{n}}",
    messengerCut: "Pinuputol ito ng Messenger sa {{max}} na character.",
    setMessengerLabel: "Maglagay ng Messenger label",
    allowTyping: "Hayaang mag-type ang bisita",
    allowTypingHint: "Ipapasa sa assistant ang anumang tina-type.",
    question: "Tanong",
    fieldLabel: "Label ng field",
    fieldLabelHint: "Ipinapakita sa input sa chat.",
    answerType: "Uri ng sagot",
    answerTypeFixed: "Itinakda ng kung saan ise-save ang sagot.",
    fieldTypes: { text: "Maikling teksto", email: "Email", phone: "Telepono", number: "Numero", date: "Petsa", dropdown: "Dropdown" },
    saveTo: "I-save ang sagot sa",
    saveTargets: { visitor_name: "Pangalan ng bisita", visitor_email: "Email ng bisita", visitor_phone: "Telepono ng bisita", lead: "Field ng lead", var: "Sa run na ito lang", attribute: "Custom attribute" },
    saveKey: "Pangalan ng field",
    saveKeyHint: "Maliliit na letra, numero at _.",
    choices: "Mga pagpipilian",
    choicesHint: "Isa bawat linya, hanggang {{max}}.",
    personalData: "Ise-save ang mga personal na detalye sa bisita o lead at sakop ng inyong privacy notice.",
    required: "Kailangang sumagot ang bisita",
    requiredHint: "I-off para puwede nilang laktawan.",
    skipIfKnown: "Laktawan kung alam na natin",
    skipIfKnownHint: "Hindi na tatanungin ulit ang mga bumabalik na bisita.",
    answerIntro: "Sasagot lang mula sa inyong mga dokumento, may mga source. Kung hindi kaya, magpapatuloy ang run sa exit na pipiliin ninyo.",
    guidance: "Dagdag na gabay",
    guidanceHint: "Sabihin sa assistant kung ano ang bibigyang-diin. Mula pa rin sa inyong mga dokumento ang sagot.",
    exits: "Ano ang susunod",
    exitContinue: "Ituloy sa susunod na hakbang",
    exitsHint: "Kapag walang piniling path, itutuloy ang run sa susunod na hakbang, o tatapusin kung wala na.",
    bookingAction: "Booking action",
    bookingActionHint: "Sa action galing ang oras, mga field ng form at ang calendar invite.",
    leadAction: "Lead form",
    leadActionHint: "Sa Capture lead action galing ang mga field.",
    actionMissing: "Hindi nahanap ang action",
    noActions: "Wala pang mapagpipilian.",
    openActions: "Buksan ang Actions",
    handTo: "Ipasa kay/sa",
    handToHint: "Sinusunod ng default ang inyong routing rules.",
    handTargets: { default: "Ang default na routing", team: "Isang team", user: "Isang tao" },
    handReason: "Tala para sa inyong team",
    handReasonHint: "Makikita ito ng staff kapag binuksan ang usapan.",
    replyTimeHelp: "Sasabihin sa bisita kung kailan aasahan ang sagot, ayon sa inyong office hours. Walang kailangang itakda.",
  },
  conditions: {
    groups: { ...workflowsP3.conditionGroups, ...workflowsP4.conditionGroups, ...workflowsConnectorStep.conditionGroups, ...workflowsService.conditionGroups, ...workflowsCustomer.conditionGroups },
    attributeGroups: workflowsP3.attributeGroups,
    choose: workflowsTriggers.conditionChoices.choose,
    choices: {
      event_changed_by: workflowsTriggers.conditionChoices.event_changed_by,
      source_notice: workflowsTriggers.conditionChoices.source_notice,
      source_run_kind: workflowsTriggers.conditionChoices.source_run_kind,
      ...workflowsService.conditionChoices,
      ...workflowsCustomer.conditionChoices,
    },
    add: "Magdagdag ng kondisyon",
    addGroup: "Magdagdag ng OR group",
    and: "at",
    or: "o",
    remove: "Alisin ang kondisyon",
    field: "Field",
    operator: "Operator",
    value: "Value",
    listHint: "Isa bawat linya.",
    yes: "Oo",
    no: "Hindi",
    fields: {
      ...workflowsP3.conditionFields,
      ...workflowsTriggers.conditionFields,
      ...workflowsService.conditionFields,
      ...workflowsCustomer.conditionFields,
      conversation_channel: "Channel",
      conversation_agent_id: "Agent",
      conversation_status: "Estado ng usapan",
      conversation_tags: "Tags ng usapan",
      conversation_language: "Wika",
      conversation_topic: "Paksa",
      conversation_assigned: "May assignee",
      conversation_team_id: "Team",
      conversation_message_count: "Bilang ng mensahe",
      message_text: "Teksto ng mensahe",
      message_role: "Mensahe mula sa",
      lead_stage: "Stage ng lead",
      lead_interest: "Interes ng lead",
      lead_source: "Pinagmulan ng lead",
      lead_has_owner: "May may-ari ang lead",
      booking_service: "Serbisyo ng booking",
      booking_status: "Estado ng booking",
      visitor_email_known: "Alam ang email ng bisita",
      visitor_verified: "Beripikado ang bisita",
      customer_tier: "Tier ng customer",
      customer_account_status: "Katayuan ng account ng customer",
      time_office_hours: "Bukas ang opisina",
      time_weekday: "Araw",
      time_hour: "Oras (0–23)",
      event_reason: "Dahilan",
      intent_topic: "Natukoy na paksa",
      intent_action: "Natukoy na pakay",
    },
    operators: {
      is: "ay",
      is_not: "ay hindi",
      is_any_of: "ay alinman sa",
      is_none_of: "ay wala sa",
      contains: "naglalaman ng",
      not_contains: "hindi naglalaman ng",
      contains_any_of: "naglalaman ng alinman sa",
      starts_with: "nagsisimula sa",
      is_set: "may laman",
      is_not_set: "walang laman",
      gt: "ay higit sa",
      lt: "ay kulang sa",
    },
  },
  runs: {
    ...workflowsP4.runs,
    title: "Mga run",
    empty: "Wala pang run. Lalabas dito ang mga run kapag tumakbo ang trigger.",
    started: "Nagsimula",
    status: "Estado",
    path: "Path",
    version: "Bersyon",
    view: "Tingnan",
    statuses: { running: "Tumatakbo", waiting: "Naghihintay", delegated: "Hawak ng assistant", completed: "Natapos", failed: "Pumalya", cancelled: "Kinansela", skipped: "Hindi nagsimula" },
    endedReason: "Natapos dahil",
    reasons: {
      ...workflowsVerify.runs.reasons,
      ...workflowsContent.reasons,
      endpoint_unavailable: "Hindi magagamit ang endpoint: naka-off o nabura ito.",
      too_many_webhooks: "Naipadala na ng run na ito ang pinakamaraming webhook na pinapayagan.",
      body_not_json_object: "Hindi JSON object ang laman ng mensahe.",
    },
    endedReasons: {
      ...workflowsP4.runs.endedReasons,
      ...workflowsVerify.runs.endedReasons,
      finished: "Narating nito ang dulo.",
      handed_off: "Ipinasa ito sa isang tao.",
      typed_text: "Nag-type ng sariling mensahe ang bisita.",
      invalid_answers: "Hindi tumugma sa tanong ang mga sagot.",
      staff_replied: "Sumagot ang isang miyembro ng team.",
      takeover: "Pumalit ang isang miyembro ng team.",
      resolved: "Nalutas ang usapan.",
      closed: "Isinara ang usapan.",
      expired: "Nag-expire ang run.",
      failed: "Pumalya ang isang step.",
      interrupted: "Naputol ang isang paghihintay.",
      quota: "Naubos na ang buwanang limit ng workflow run, kaya hindi nagsimula ang run na ito.",
      plan: "Hindi na kasama sa plan mo ang trigger na ito, kaya hindi nagsimula ang run na ito.",
    },
    outcomes: { done: "Tapos", skipped: "Nilaktawan", failed: "Pumalya" },
    detailTitle: "Run",
    openConversation: "Buksan ang usapan",
    openLead: "Buksan ang lead",
    error: "Dahilan",
    loadMore: "Mas lumang run",
    detailFailed: "Hindi na-load ang run",
    detailFailedBody: "Tingnan ang inyong koneksyon, pagkatapos ay subukan muli.",
  },
  overview: {
    ...workflowsP4.overview,
    range: "Huling {{days}} araw",
    sent: "Naipadala",
    sentHint: "mga run na nagsimula",
    completed: "Natapos",
    completedHint: "umabot sa dulo",
    failed: "Pumalya",
    failedHint: "may pumalyang hakbang",
    steps: "Mga hakbang",
    reached: "Naabot",
    failedSteps: "Pumalya",
    noData: "Wala pang run sa panahong ito.",
  },
  hook: workflowsP4.hook,
  goal: workflowsP4.goal,
  canvas: workflowsP4.canvas,
  connectors: { ...workflowsConnectors, ...workflowsConversation.connectors },
  connector: workflowsConnectorStep.connector,
  versions: workflowsP4.versions,
  analysis: { ...workflowsP4.analysis, outcomes: { ...workflowsP4.analysis.outcomes, ...workflowsVerify.analysis.outcomes, ...workflowsContent.outcomes } },
  translations: { ...workflowsP4.translations, kinds: { ...workflowsP4.translations.kinds, ...workflowsContent.translationKinds } },
  setup: workflowsSetup,
  addStep: workflowsAddStep,
  extras: workflowsExtras,
  attributeValues: workflowsTriggers.attributeValues,
  article: workflowsContent.article,
  summaryNote: workflowsContent.summaryNote,
  inbox: workflowsP4.inbox,
  defaults: { ...workflowsDefaults, service: workflowsService.list },
  service: workflowsService.service,
  system: workflowsSystem.system,
  regression: workflowsRegression,
};
